













































































































































import mixins, { ExtractVue } from '@/utils/mixins';
import { provide as RegistrableProvide } from '@/mixins/registrable';
import { inject as RegistrableInject } from '@/mixins/registrable';
import BindsAttrs from '@/mixins/binds-attrs';
import {
  ComputationBinaryOperators,
  ComputationNodeSubtype,
  ComputationNodeType,
  ExpressionNodeTree,
  IServiceComputationNode,
  ServiceComputationNode,
  ServiceComputationNodeFieldError,
  ServicePriceComputation,
  OperatorNodeType
} from '@/models';

import Vue, { PropType, VNode, VNodeChildren } from 'vue';
import { PropValidator } from 'vue/types/options';
import { cloneDeep } from 'lodash';
import { VNodeData } from 'vue/types/umd';
import { createRange, deepEqual } from '@/utils/helpers';
import ComputationOperandNode from './ComputationOperandNode.vue';
import ComputationOperatorNode from './ComputationOperatorNode.vue';
import VNodes from './VNodes.vue';

const baseMixins = mixins(
  Vue,
  BindsAttrs,
  RegistrableProvide('formExpression'),
  RegistrableInject<'form', any>('form')
);

type ErrorBag = Record<number, boolean>;
type VExpressionInstance = InstanceType<
  typeof ComputationOperandNode | typeof ComputationOperatorNode
> & { _uid: any; validate(): boolean; reset(): void };
type Watchers = {
  _uid: number;
  valid: () => void;
  shouldValidate: () => void;
};

/* @vue/component */
const ComputationExpressionTree = baseMixins
  .extend<ExtractVue<typeof baseMixins>>({ functional: false })
  .extend({
    components: { ComputationOperandNode, ComputationOperatorNode, VNodes },
    name: 'ComputationExpressionTree',

    provide(): object {
      return { formExpression: this };
    },

    props: {
      fieldErrors: {
        required: false,
        type: Array as PropType<ServiceComputationNodeFieldError[]>,
        default: () => []
      },

      value: {
        required: false,
        type: Array as PropType<ExpressionNodeTree>,
        default: () => []
      } as PropValidator<ExpressionNodeTree | IServiceComputationNode>,

      level: {
        required: false,
        type: Number,
        default: 0
      },

      index: {
        required: false,
        type: Number,
        default: -1
      },

      parentIsDisabled: {
        required: false,
        type: Boolean,
        default: false
      },

      selectionType: {
        type: String as PropType<'leaf' | 'independent'>,
        default: 'leaf',
        validator: (v: string) => ['leaf', 'independent'].includes(v)
      },

      disablePerNode: {
        required: false,
        type: Boolean,
        default: false
      }
    },

    data() {
      return {
        writeIndex: this.index,
        inputs: [] as VExpressionInstance[],
        watchers: [] as Watchers[],
        errorBag: {} as ErrorBag,

        canAddOperation: false,
        OperatorNodeType
      };
    },

    computed: {
      internalValue: {
        get(): ExpressionNodeTree {
          return this.value as ExpressionNodeTree;
        },
        set(value: ExpressionNodeTree) {
          // hack for passing multiple function on the emit values
          this.$emit('input', value);
          this.$forceUpdate();
        }
      },

      canAddSubExpression(): boolean {
        return this.level <= ServicePriceComputation.MAX_EXPRESSION_TREE_DEPTH;
      },

      disabled(): boolean {
        return (
          !this.disablePerNode &&
          this.parentIsDisabled &&
          this.selectionType === 'leaf'
        );
      },

      hasChildren(): boolean {
        return this.children.length > 0;
      },

      children(): ExpressionNodeTree {
        if (Array.isArray(this.value)) return this.value;
        return [];
      },

      hasError(): boolean {
        return !this.canAddOperation;
      },

      inputsWithError(): VExpressionInstance[] {
        return this.inputs.filter(input => {
          return !!this.errorBag[input._uid];
        });
      }
    },

    watch: {
      errorBag: {
        handler(val) {
          const errors = Object.values(val).every(v => v === false);
          this.canAddOperation = !!errors;
          this.$emit('update:canAddOperation', this.canAddOperation);
          if (!errors) {
            // to enforce errors on inner expressions
            this.findInputsWithError(this.inputsWithError);
          }
        },
        deep: true,
        immediate: true
      }
    },

    methods: {
      updateCanAddOperation(value: boolean) {
        this.canAddOperation = value;
        this.$emit('update:canAddOperation', value);
      },

      addNode(tree: ExpressionNodeTree) {
        const treeClone = cloneDeep(tree);

        const lastNodeInTree = tree[tree.length - 1];

        const startBracketNode = new ServiceComputationNode({
          type: ComputationNodeType.operator,
          subtype: ComputationBinaryOperators.start_bracket
        });
        const closeBracketNode = new ServiceComputationNode({
          type: ComputationNodeType.operator,
          subtype: ComputationBinaryOperators.close_bracket
        });
        const operatorNode = new ServiceComputationNode({
          type: ComputationNodeType.operator,
          subtype: ComputationBinaryOperators.add
        });
        const operandNode = new ServiceComputationNode({
          type: ComputationNodeType.operand
        });

        const operation = [
          startBracketNode,
          cloneDeep(operandNode),
          closeBracketNode
        ];
        if (!lastNodeInTree) {
          treeClone.push(...operation);
        } else if (!Array.isArray(lastNodeInTree)) {
          const operation = [operatorNode, operandNode];
          const insertIndex = tree.length - 1;

          if (lastNodeInTree.isCloseBracket()) {
            treeClone.splice(insertIndex, 0, ...operation);
          } else {
            treeClone.push(...operation);
          }
        } else {
          const lastNodeInLastTree = lastNodeInTree[lastNodeInTree.length - 1];

          if (
            !Array.isArray(lastNodeInLastTree) &&
            lastNodeInLastTree.isCloseBracket()
          ) {
            treeClone.push(operatorNode);
            treeClone.push(...operation);
          } else {
            const operation = [operatorNode, operandNode];
            treeClone.push(...operation);
          }
        }
        // if new tree is difference from prev tree
        if (!deepEqual(tree, treeClone)) {
          this.setTree(treeClone);
        }
      },
      addSubNode(tree: ExpressionNodeTree, type: OperatorNodeType) {
        const treeClone = cloneDeep(tree);

        const startBracketNode = new ServiceComputationNode({
          type: ComputationNodeType.operator,
          subtype: ComputationBinaryOperators.start_bracket
        });
        const closeBracketNode = new ServiceComputationNode({
          type: ComputationNodeType.operator,
          subtype: ComputationBinaryOperators.close_bracket
        });
        const operatorNode = new ServiceComputationNode({
          type: ComputationNodeType.operator,
          subtype: ComputationBinaryOperators.add
        });
        const unaryOperator = new ServiceComputationNode({
          type: ComputationNodeType.operator,
          subtype: ComputationBinaryOperators.ceil
        });
        const operandNode = new ServiceComputationNode({
          type: ComputationNodeType.operand,
          subtype: ComputationNodeSubtype.constant
        });
        let subExpression: ExpressionNodeTree = [
          startBracketNode,
          operandNode,
          closeBracketNode
        ];

        if (this.level === 0) {
          const innerSubexpression: ExpressionNodeTree = [
            startBracketNode,
            operandNode,
            closeBracketNode
          ];
          subExpression = [
            cloneDeep(startBracketNode),
            innerSubexpression,
            cloneDeep(closeBracketNode)
          ];
          if (type == OperatorNodeType.unary) {
            subExpression.splice(1, 0, unaryOperator);
          }
          treeClone.push(subExpression);
        } else {
          treeClone.splice(treeClone.length - 1, 0, cloneDeep(operatorNode));
          if (type == OperatorNodeType.unary) {
            treeClone.splice(treeClone.length - 1, 0, unaryOperator);
          }
          treeClone.splice(treeClone.length - 1, 0, cloneDeep(subExpression));
        }

        this.setTree(treeClone);
      },

      removeNodeRow(index: number, deleteCount = 2) {
        const newTree: ExpressionNodeTree = cloneDeep(this.internalValue);
        if (index == 1) {
          newTree.splice(index, 1);
          this.clearFieldError(index);
        } else {
          newTree.splice(index - 1, deleteCount);
          this.clearFieldError(index - 1);
        }

        this.setTree(newTree);
      },

      removeSubNode() {
        this.$emit('removeLastOperators');
      },

      removeLastOperatorsFromGroup(index: number) {
        const newTree: ExpressionNodeTree = cloneDeep(this.internalValue);
        const operandNode = new ServiceComputationNode({
          type: ComputationNodeType.operand,
          subtype: ComputationNodeSubtype.constant
        });
        this.$delete(newTree, index);
        this.clearFieldError(index);
        let deleteIterationLoop = 1;
        if (index > 0) {
          while (deleteIterationLoop > 0) {
            const lastNode = newTree[index - deleteIterationLoop];
            if (!lastNode) {
              break;
            }
            const isNodeInValidOperand =
              Array.isArray(lastNode) || lastNode.isOperand();
            if (isNodeInValidOperand) {
              break;
            } else {
              if ((lastNode as IServiceComputationNode).isStartBracket()) {
                // this is only fullfilled by first node group is a unary group that starts
                // the expression
                newTree.splice(1, 0, cloneDeep(operandNode));
                break;
              }
              this.$delete(newTree, index - deleteIterationLoop);
              this.clearFieldError(index - deleteIterationLoop);
              deleteIterationLoop++;
            }
          }
        }
        this.setTree(newTree);
      },

      /**
       * Best to update the value by explicity assinging the internalValue
       */
      setTree(tree: ExpressionNodeTree) {
        this.internalValue = tree;
      },

      setNodeList(value: ExpressionNodeTree, index: number) {
        const newTree: ExpressionNodeTree = cloneDeep(this.internalValue);
        this.$set(newTree, index, value);
        this.setTree(newTree);
        this.clearFieldError(index);
      },

      setUranyNodeList(value: IServiceComputationNode, index: number) {
        const newTree: ExpressionNodeTree = cloneDeep(this.internalValue);

        const startBracketNode = new ServiceComputationNode({
          type: ComputationNodeType.operator,
          subtype: ComputationBinaryOperators.start_bracket
        });

        if (value.isUnaryOperator()) {
          // insert bracket after first node in group
          this.$set(newTree, index, value);
          newTree.splice(index + 1, 0, startBracketNode);
        } else {
          // remove current first node in group
          newTree.splice(index, 1);
        }

        this.setTree(newTree);
        this.clearFieldError(index);
      },

      clearFieldError(index: number) {
        if (this.fieldErrors[index]) {
          this.fieldErrors[index].clearErrors();
        }
      },

      // watch inner components for validation
      watchInput(input: Vue & { _uid: any }): Watchers {
        const watcher = (input: Vue & { _uid: any }): (() => void) => {
          return input.$watch(
            'hasError',
            (val: boolean) => {
              this.$set(this.errorBag, input._uid, val);
            },
            {
              immediate: true
            }
          );
        };

        const watchers: Watchers = {
          _uid: input._uid,
          valid: () => {},
          shouldValidate: () => {}
        };

        watchers.valid = watcher(input);

        return watchers;
      },

      register(input: VExpressionInstance) {
        this.inputs.push(input);
        this.watchers.push(this.watchInput(input));
      },
      unregister(input: VExpressionInstance) {
        const found = this.inputs.find(i => i._uid === input._uid);

        if (!found) return;

        const unwatch = this.watchers.find(i => i._uid === found._uid);
        if (unwatch) {
          unwatch.valid();
          unwatch.shouldValidate();
        }

        this.watchers = this.watchers.filter(i => i._uid !== found._uid);
        this.inputs = this.inputs.filter(i => i._uid !== found._uid);
        this.$delete(this.errorBag, found._uid);
      },

      /** @public */
      validate(): boolean {
        return this.inputs.filter(input => !input.validate()).length === 0;
      },

      /** @public */
      reset(): void {
        this.inputs.forEach(input => input.reset());
        this.resetErrorBag();
      },

      resetErrorBag() {
        if (this.form.lazyValidation) {
          // Account for timeout in validatable
          this.$nextTick(() => {
            this.errorBag = {};
          });
        }
      },

      findInputsWithError(inputs: VExpressionInstance[]) {
        inputs.forEach(input => {
          input.validate();
        });
      },

      isEvenIndex(index: number) {
        return index % 2 === 0;
      },

      // Components

      genVTreeViewNodeLevel(level: number, data?: VNodeData) {
        return createRange(level).map(() =>
          this.$createElement('div', {
            staticClass: 'v-treeview-node__level',
            ...data
          })
        );
      },

      genExpressionTree(index: number) {
        return this.$createElement(ComputationExpressionTree, {
          key: `${index}_expression_tree`,
          attrs: {
            disabled: this.disabled,
            index: index,
            value: this.internalValue[index],
            fieldErrors: this.fieldErrors,
            level: this.level + 1,
            parentIsDisabled: this.parentIsDisabled,
            disablePerNode: this.disablePerNode,
            selectionType: this.selectionType
          },

          on: {
            input: (value: ExpressionNodeTree) =>
              this.setNodeList(value, index),
            removeLastOperators: () => this.removeLastOperatorsFromGroup(index)
          }
        });
      },

      genOperatorNode(index: number, type: OperatorNodeType) {
        const isLastOperatorNode =
          (this.internalValue.length - 1 - index) % 3 != 2;

        return this.$createElement(
          'div',
          {
            staticClass: `v-treeview-node__operator__${type}`,
            class: 'pb-2 pr-2 d-flex flex-row'
          },
          [
            this.$createElement(
              'div',
              {
                staticClass: 'v-treeview-node__child-operator',
                class: 'd-flex flex-row'
              },
              this.genVTreeViewNodeLevel(this.level)
            ),
            this.$createElement(
              'div',
              { style: 'min-width: 170px; position: relative;' },
              [
                this.$createElement(ComputationOperatorNode, {
                  staticClass: 'v-treeview-node__operator-first',
                  class: {
                    'v-treeview-node__operator-last': isLastOperatorNode
                  },
                  attrs: {
                    value: this.internalValue[index],
                    fieldError: this.fieldErrors[index],
                    disabled: this.disabled || index == 0,
                    isFirst: index == 0,
                    type: type
                  },
                  on: {
                    input: (value: ExpressionNodeTree) =>
                      this.setNodeList(value, index),
                    'unary:input': (value: IServiceComputationNode) =>
                      this.setUranyNodeList(value, index)
                  }
                })
              ]
            )
          ]
        );
      },

      genOperandNode(index: number) {
        return this.$createElement(
          'div',
          {
            staticClass: 'v-treeview-node__operand',
            class: 'pb-2 overflow-x-scroll'
          },
          [
            this.$createElement(ComputationOperandNode, {
              attrs: {
                fieldError: this.fieldErrors[index],
                value: this.internalValue[index],
                disabled: this.disabled
              },
              on: {
                input: (value: ExpressionNodeTree) =>
                  this.setNodeList(value, index)
              }
            })
          ]
        );
      },

      genDeleteNodeRowButton(index: number, deleteCount = 2) {
        return this.$createElement(
          'div',
          {
            staticClass: 'v-treeview-node__close_button',
            class: 'd-flex flex-row align-start justify-center',
            style: 'margin-top: 2px;'
          },
          [
            this.$createElement(
              'v-btn',
              {
                attrs: { disabled: this.disabled, icon: true, color: 'error' },
                on: {
                  click: () => this.removeNodeRow(index, deleteCount)
                }
              },
              [this.$createElement('v-icon', ['mdi-minus-circle'])]
            )
          ]
        );
      },

      genVTreeViewNodeChildren() {
        const children: VNodeChildren = [];
        let writeIndex = 0;
        for (
          let readIndex = 0;
          readIndex < this.internalValue.length;
          readIndex++
        ) {
          if (writeIndex == readIndex) {
            const currentNode = this.internalValue[readIndex];
            const nextNode = this.internalValue[readIndex + 1];

            if (Array.isArray(currentNode)) {
              children.push(this.genExpressionTree(writeIndex));
              writeIndex++;
            }
            // Generate a new row to display 2 nodes
            else if (!currentNode.isCloseBracket()) {
              if (currentNode.isOperator()) {
                if (currentNode.isUnaryOperator()) {
                  children.push(
                    this.genOperatorNode(writeIndex, OperatorNodeType.unary)
                  );
                  children.push(this.genDeleteNodeRowButton(writeIndex + 1, 1));
                } else {
                  children.push(
                    this.genOperatorNode(writeIndex, OperatorNodeType.binary)
                  );
                }
                writeIndex++;
              }

              if (!Array.isArray(nextNode)) {
                if (nextNode.isOperand()) {
                  children.push(this.genOperandNode(writeIndex));
                  if (writeIndex > 1) {
                    children.push(this.genDeleteNodeRowButton(writeIndex));
                  }
                  writeIndex++;
                }
              }
            }
          }
        }

        return this.$createElement(
          'div',
          {
            staticClass: 'v-treeview-node__children',
            class: 'd-flex flex-wrap mt-2 flex-grow-1',
            style: 'flex: 1 1 100%; position: relative; '
          },
          children
        );
      }
    },

    created() {
      this.form && this.form.register(this);
    },

    beforeDestroy() {
      this.form && this.form.unregister(this);
    },

    beforeMount() {
      this.validate();
    }
  });

export default ComputationExpressionTree;
